/* eslint-disable max-len */
/* eslint-disable no-console */
import {Button, Flex, Col, Spin} from 'antd';
import React, {useEffect, useState} from 'react';

import {ReactComponent as EnterprisePlan} from '../../assets/icons/EnterprisePlan.svg';
import {ReactComponent as FreePlan} from '../../assets/icons/FreePlan.svg';
import {ReactComponent as PlusPlan} from '../../assets/icons/PlusPlan.svg';
import {ReactComponent as ProPlan} from '../../assets/icons/ProPlan.svg';
import {RAZORPAY_KEY_ID} from '../../config';
import {subscriptionPlanTypes} from '../../constants/subscriptionConstants';
import subscriptionDetails from '../../services/modules/OM/subscriptionDetails';
import {
  initiatePayment,
  verifyPayment
} from '../../services/modules/Payment/razorPay';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import showMessage from '../../utils/showMessage';
import {isOnboardingDone} from '../../utils/SubscriptionHelper';

import {contactForm} from './constants/commonConstants';
import FeatureListing from './FeatureListing';

const PricingPlansDetails: React.FC<{data: any}> = ({data}) => {
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [scriptLoaded, setScriptLoaded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const companyDetails = useCompanyStore.getState()?.companyDetails;
  const activePlan = useCompanyStore((s) => s.subscription?.planType);

  // Load Razorpay script
  useEffect(() => {
    window.scrollTo(0, 0);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => {
      setScriptLoaded(true);
    };
    script.onerror = () => {
      showMessage({
        message: 'Something went wrong please try again later.',
        status: 'error'
      });
      setSelectedPlan('');
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const findPlanIcon = (planType: string) => {
    switch (planType) {
      case subscriptionPlanTypes.FREE:
      case subscriptionPlanTypes.FREE_TRIAL:
      case subscriptionPlanTypes.READ_ONLY:
        return <FreePlan />;
      case subscriptionPlanTypes.PRO:
        return <ProPlan />;
      case subscriptionPlanTypes.PLUS:
        return <PlusPlan />;
      case subscriptionPlanTypes.ENTERPRISE:
        return <EnterprisePlan />;
      default:
        return <></>;
    }
  };

  const featureListingData = (plans: any) => {
    const data = [];

    data.push({
      isPresent: true,
      label: formatScopes(plans?.scopes)
    });

    data.push({
      isPresent:
        plans?.noOfFacilities > 0 || plans?.noOfFacilities === 'Unlimited',
      label: `${plans?.noOfFacilities} Facilities`
    });

    data.push({
      isPresent:
        plans?.stakeHolderUsers > 0 || plans?.stakeHolderUsers === 'Unlimited',
      label:
        plans?.stakeHolderUsers === 0
          ? 'Stakeholder Users'
          : `${plans?.stakeHolderUsers} Stakeholder Users`
    });

    data.push({
      isPresent: plans?.CAReport !== '',
      label:
        plans?.CAReport === '' ? 'Carbon Accounting Report' : plans?.CAReport
    });

    data.push({
      isPresent: plans?.BRSRReport !== '',
      label: `BRSR ${plans?.BRSRReport === '' ? '' : plans?.BRSRReport}`
    });

    data.push({
      isPresent: plans?.targetSetting !== '',
      label: `${
        plans?.targetSetting === '' ? 'Target Setting' : plans?.targetSetting
      }`
    });

    data.push({
      isPresent: plans?.AICredits > 0 || plans?.AICredits === 'Unlimited',
      label:
        plans?.AICredits === 0
          ? 'AI Credits'
          : plans?.AICredits === 'Unlimited'
            ? 'Unlimited AI Credits'
            : `Up to ${plans?.AICredits} AI Credits`
    });

    data.push({isPresent: plans?.phoneSupport, label: 'Phone Support'});

    return data;
  };

  const handlePayment = async (selectedPlanType: 'PRO' | 'PLUS') => {
    setSelectedPlan(selectedPlanType);

    if (!scriptLoaded) {
      setSelectedPlan('');
      showMessage({
        message: 'Something went wrong please try again later.',
        status: 'error'
      });

      return;
    }

    try {
      // Create subscription
      const response: any = await initiatePayment(selectedPlanType);

      const {orderId, amount, currency} = response;

      const options: RazorpayOptions = {
        key: `${RAZORPAY_KEY_ID}`,
        order_id: orderId, // Use subscription_id instead of order_id
        amount,
        currency,
        name: 'Bharat Carbon',
        description: `${selectedPlanType} Plan`,
        handler: async (response: RazorpayResponse) => {
          try {
            setSelectedPlan(selectedPlanType);
            setLoading(true);
            // Verify payment (same as previous)
            await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            }).then(async (verifyResponse) => {
              if (verifyResponse?.success) {
                await subscriptionDetails();
                setSelectedPlan('');

                showMessage({
                  message: 'Subscription successful!',
                  status: 'success'
                });
                setLoading(false);
              } else {
                setLoading(false);
                setSelectedPlan('');
                showMessage({
                  message: 'Payment verification failed',
                  status: 'error'
                });
              }
            });
          } catch (ex) {
            setLoading(false);
            setSelectedPlan('');
            showMessage({
              message: 'Payment verification failed',
              status: 'error'
            });
          }
        },
        prefill: {
          name: `${companyDetails?.companyName}`,
          email: `${companyDetails?.businessEmail}`,
          contact: `${companyDetails?.telephone}`
        },
        theme: {color: '#3399cc'},
        modal: {
          ondismiss: () => {
            setSelectedPlan('');
            showMessage({
              message: 'Payment cancelled',
              status: 'info'
            });
          }
        }
      };

      // Step 4: Open Razorpay Checkout
      const rzp = new (window as any).Razorpay(options);

      rzp.on('payment.failed', (response: any) => {
        setSelectedPlan('');
        showMessage({
          message: `Payment failed: ${response.error.description}`,
          status: 'warning'
        });
      });
      rzp.open();
    } catch (error) {
      setSelectedPlan('');
      showMessage({
        message: 'Error initiating subscription',
        status: 'error'
      });
    }
  };

  const formatScopes = (scopes: any) => {
    if (scopes.length === 1) return scopes[0];
    if (scopes.length === 2) return `${scopes[0]} and ${scopes[1]}`;

    const allButLast = scopes.slice(0, -1).join(', ');
    const last = scopes[scopes.length - 1];

    return `${allButLast} and ${last}`;
  };

  return (
    <>
      {loading ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(255,255,255,0.92)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <Spin size="large" tip="Processing payment…" />
          <div style={{marginTop: 16, fontSize: 16, color: '#555'}}>
            Please do not close or refresh the page.
          </div>
        </div>
      ) : null}
      {data?.map((planItems: any, index: number) => {
        return (
          <>
            <Col key={`plan-${index}-${planItems?.planType}`} span={6}>
              <Flex
                className="planMainContainer"
                vertical
                style={{
                  border:
                    planItems?.planType === subscriptionPlanTypes.PLUS
                      ? '2px solid #719A5D'
                      : '2px solid #f3f4f8',
                  background:
                    planItems?.planType === subscriptionPlanTypes.PLUS
                      ? '#eef4ef'
                      : 'inherited',
                  minHeight: '700px',
                  height: '100%',
                  position: 'relative'
                }}
              >
                {planItems?.planType === subscriptionPlanTypes.PLUS && (
                  <div className="mostPopular">Most Popular</div>
                )}

                <Flex style={{minHeight: '80px'}}>
                  <Flex gap={10} vertical={false}>
                    <div
                      className="planIconContainer"
                      style={
                        planItems?.planType === subscriptionPlanTypes.PLUS
                          ? {background: '#d3eec6'}
                          : {}
                      }
                    >
                      {findPlanIcon(planItems?.planType)}
                    </div>
                    <Flex vertical>
                      <div className="planTitleText">
                        {planItems?.planLabel}
                      </div>
                      <div className="planInfoText">
                        {planItems?.planInfoText}
                      </div>
                    </Flex>
                  </Flex>
                </Flex>

                <Flex vertical style={{minHeight: '110px'}}>
                  <Flex vertical={false} align="center" gap={2}>
                    {planItems.planType === subscriptionPlanTypes.ENTERPRISE ? (
                      <div className="price">Get in touch!</div>
                    ) : (
                      <>
                        <div className="price">
                          ₹ {planItems?.pricePerMonth}
                        </div>
                        <div className="perMonthText">
                          / {planItems.additionalProps.priceFrequencyIn}
                        </div>
                      </>
                    )}
                  </Flex>
                  <div className="billed">
                    {planItems.planType === subscriptionPlanTypes.PRO ||
                    planItems.planType === subscriptionPlanTypes.PLUS
                      ? `Billed ${planItems?.billed}`
                      : ''}
                  </div>
                  {planItems.planType === subscriptionPlanTypes.PRO ||
                  planItems.planType === subscriptionPlanTypes.PLUS ? (
                    <div className="billed">
                      <b>2nd Year: </b>
                      {planItems?.yearWiseDiscount2ndYear}, <b>3rd Year: </b>
                      {planItems?.yearWiseDiscount3rdYear} and{' '}
                      {planItems?.yearWiseDiscountGST} GST
                    </div>
                  ) : (
                    ''
                  )}
                </Flex>
                {activePlan === subscriptionPlanTypes.READ_ONLY &&
                planItems.planType === subscriptionPlanTypes.FREE_TRIAL ? (
                  <Button
                    disabled={true}
                    className={`capsuleButton ${
                      planItems?.planType === activePlan && 'currentPlan'
                    }`}
                  >
                    Expired Plan
                  </Button>
                ) : (
                  <Button
                    // type="primary"
                    disabled={planItems?.planType === activePlan}
                    // shape="round"
                    className={`capsuleButton ${
                      planItems?.planType === activePlan && 'currentPlan'
                    }`}
                    onClick={() => {
                      if (!isOnboardingDone()) {
                        return;
                      }

                      if (
                        planItems?.planType === 'PRO' &&
                        (activePlan === subscriptionPlanTypes.FREE ||
                          activePlan === subscriptionPlanTypes.FREE_TRIAL ||
                          activePlan === subscriptionPlanTypes.READ_ONLY)
                      ) {
                        void handlePayment('PRO');
                      } else if (
                        planItems?.planType === 'PLUS' &&
                        (activePlan === subscriptionPlanTypes.FREE ||
                          activePlan === subscriptionPlanTypes.FREE_TRIAL ||
                          activePlan === subscriptionPlanTypes.READ_ONLY ||
                          activePlan === subscriptionPlanTypes.PRO)
                      ) {
                        void handlePayment('PLUS');
                      } else if (planItems?.planType === 'ENTERPRISE') {
                        window.open(
                          contactForm,
                          '_blank',
                          'noopener,noreferrer'
                        );
                      } else {
                        showMessage({
                          message: `Your active plan is ${activePlan}`,
                          status: 'info'
                        });
                      }
                    }}
                    loading={
                      selectedPlan?.toLocaleLowerCase() === planItems?.planType
                    }
                  >
                    {selectedPlan?.toLocaleLowerCase() === planItems?.planType
                      ? 'Processing...'
                      : activePlan === planItems?.planType
                        ? 'Current Plan'
                        : planItems?.planButtonTitle}
                  </Button>
                )}
                <Flex vertical gap={10}>
                  <FeatureListing
                    data={featureListingData(planItems)}
                    isReadOnly={
                      activePlan === subscriptionPlanTypes.READ_ONLY &&
                      planItems.planType === subscriptionPlanTypes.FREE_TRIAL
                    }
                  />
                </Flex>
              </Flex>
            </Col>
          </>
        );
      })}
    </>
  );
};

export default PricingPlansDetails;
